import {
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowsOutCardinal,
  CircleNotch,
  ClockClockwise,
  CubeFocus,
  DownloadSimple,
  LineSegment,
  LinkSimple,
  MagnifyingGlass,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
} from "@phosphor-icons/react";
import { Button, Separator, Toggle, Tooltip } from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { useState } from "react";

import { usePrintCoverLetter } from "@/client/services/cover-letter";
import { useBuilderStore } from "@/client/stores/builder";
import { useCoverLetterStore, useTemporalCoverLetterStore } from "@/client/stores/cover-letter";

const openInNewTab = (url: string) => {
  const safeUrl = `${url}?t=${Date.now()}`;
  const win = window.open(safeUrl, "_blank");
  if (win) win.focus();
};

export const CoverLetterBuilderToolbar = () => {
  const [panMode, setPanMode] = useState<boolean>(true);

  const setValue = useCoverLetterStore((state) => state.setValue);
  const undo = useTemporalCoverLetterStore((state) => state.undo);
  const redo = useTemporalCoverLetterStore((state) => state.redo);
  const frameRef = useBuilderStore((state) => state.frame.ref);

  const id = useCoverLetterStore((state) => state.coverLetter?.id);
  const pageOptions = useCoverLetterStore(
    (state) => state.coverLetter?.data?.metadata?.page?.options,
  );

  const { printCoverLetter, loading } = usePrintCoverLetter();

  const handlePrint = async () => {
    if (!id) return;
    const { url } = await printCoverLetter({ id });
    openInNewTab(url);
  };

  const handleZoomIn = () => frameRef?.contentWindow?.postMessage({ type: "ZOOM_IN" }, "*");
  const handleZoomOut = () => frameRef?.contentWindow?.postMessage({ type: "ZOOM_OUT" }, "*");
  const handleResetView = () => frameRef?.contentWindow?.postMessage({ type: "RESET_VIEW" }, "*");
  const handleCenterView = () =>
    frameRef?.contentWindow?.postMessage({ type: "CENTER_VIEW" }, "*");
  const handleTogglePanMode = () => {
    setPanMode(!panMode);
    frameRef?.contentWindow?.postMessage({ type: "TOGGLE_PAN_MODE", panMode: !panMode }, "*");
  };

  return (
    <motion.div className="fixed bottom-0 flex w-full items-center justify-center py-6">
      <div className="inline-flex items-center justify-center rounded-full bg-background px-4 shadow-xl">
        <Tooltip content="Undo">
          <Button size="icon" variant="ghost" className="rounded-none" onClick={() => undo()}>
            <ArrowCounterClockwise />
          </Button>
        </Tooltip>

        <Tooltip content="Redo">
          <Button size="icon" variant="ghost" className="rounded-none" onClick={() => redo()}>
            <ArrowClockwise />
          </Button>
        </Tooltip>

        <Separator orientation="vertical" className="h-9" />

        <Tooltip content={panMode ? "Scroll to Pan" : "Scroll to Zoom"}>
          <Toggle className="rounded-none" pressed={panMode} onPressedChange={handleTogglePanMode}>
            {panMode ? <ArrowsOutCardinal /> : <MagnifyingGlass />}
          </Toggle>
        </Tooltip>

        <Separator orientation="vertical" className="h-9" />

        <Tooltip content="Zoom In">
          <Button size="icon" variant="ghost" className="rounded-none" onClick={handleZoomIn}>
            <MagnifyingGlassPlus />
          </Button>
        </Tooltip>

        <Tooltip content="Zoom Out">
          <Button size="icon" variant="ghost" className="rounded-none" onClick={handleZoomOut}>
            <MagnifyingGlassMinus />
          </Button>
        </Tooltip>

        <Tooltip content="Reset Zoom">
          <Button size="icon" variant="ghost" className="rounded-none" onClick={handleResetView}>
            <ClockClockwise />
          </Button>
        </Tooltip>

        <Tooltip content="Center Artboard">
          <Button size="icon" variant="ghost" className="rounded-none" onClick={handleCenterView}>
            <CubeFocus />
          </Button>
        </Tooltip>

        <Separator orientation="vertical" className="h-9" />

        <Tooltip content="Toggle Page Break Line">
          <Toggle
            className="rounded-none"
            pressed={pageOptions?.breakLine}
            onPressedChange={(pressed) => {
              setValue("metadata.page.options.breakLine", pressed);
            }}
          >
            <LineSegment />
          </Toggle>
        </Tooltip>

        <Separator orientation="vertical" className="h-9" />

        <Tooltip content="Download PDF">
          <Button
            size="icon"
            variant="ghost"
            disabled={loading || !id}
            className="rounded-none"
            onClick={handlePrint}
          >
            {loading ? <CircleNotch className="animate-spin" /> : <DownloadSimple size={20} />}
          </Button>
        </Tooltip>
      </div>
    </motion.div>
  );
};
